export interface InputManagerOptions {
  attachWindow?: boolean;
}

export class InputManager {
  private keyState = new Map<string, boolean>();
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;
  private keyUpHandler: ((e: KeyboardEvent) => void) | null = null;
  private interactCooldown: number = 0;

  constructor(options: InputManagerOptions = {}) {
    const attach = options.attachWindow ?? (typeof window !== 'undefined');
    if (attach && typeof window !== 'undefined') {
      this.keyDownHandler = (e: KeyboardEvent) => {
        this.keyState.set(e.code, true);
      };
      this.keyUpHandler = (e: KeyboardEvent) => {
        this.keyState.set(e.code, false);
      };
      window.addEventListener('keydown', this.keyDownHandler);
      window.addEventListener('keyup', this.keyUpHandler);
    }
  }

  public setKeyState(code: string, isDown: boolean): void {
    this.keyState.set(code, isDown);
  }

  public isKeyDown(code: string): boolean {
    return this.keyState.get(code) === true;
  }

  public getMoveVector(): { x: number; z: number } {
    let forward = 0;
    let right = 0;
    if (this.isKeyDown('KeyW') || this.isKeyDown('ArrowUp')) forward += 1;
    if (this.isKeyDown('KeyS') || this.isKeyDown('ArrowDown')) forward -= 1;
    if (this.isKeyDown('KeyD') || this.isKeyDown('ArrowRight')) right += 1;
    if (this.isKeyDown('KeyA') || this.isKeyDown('ArrowLeft')) right -= 1;

    if (forward === 0 && right === 0) {
      return { x: 0, z: 0 };
    }

    const length = Math.hypot(right, forward);
    return {
      x: right / length,
      z: forward / length
    };
  }

  public isJumping(): boolean {
    return this.isKeyDown('Space');
  }

  public isDashing(): boolean {
    return this.isKeyDown('ShiftLeft') || this.isKeyDown('ShiftRight');
  }

  public isInteracting(): boolean {
    return this.isKeyDown('KeyF') || this.isKeyDown('KeyE');
  }

  public consumeInteract(dt: number): boolean {
    this.interactCooldown = Math.max(0, this.interactCooldown - dt);
    if (this.isInteracting() && this.interactCooldown <= 0) {
      this.interactCooldown = 0.35; // 350ms throttle between strikes
      return true;
    }
    return false;
  }

  public consumeSkill(): string | null {
    if (this.isKeyDown('Digit1') || this.isKeyDown('KeyQ')) return 'water_shot';
    if (this.isKeyDown('Digit2') || this.isKeyDown('KeyR')) return 'acid_spit';
    if (this.isKeyDown('Digit3') || this.isKeyDown('KeyC')) return 'body_slam';
    return null;
  }

  public dispose(): void {
    if (typeof window !== 'undefined') {
      if (this.keyDownHandler) window.removeEventListener('keydown', this.keyDownHandler);
      if (this.keyUpHandler) window.removeEventListener('keyup', this.keyUpHandler);
    }
    this.keyState.clear();
  }
}
